import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { EMPTY, Observable, of } from 'rxjs';
import { catchError, delay, expand, last, map, switchMap } from 'rxjs/operators';

import { Paginated } from '../../common/entities/mediahaven-api/paginated.entity';

import { CustomHttpParamEncoder } from '../../../core/http/entities/custom-http-param-encoder';
import { ShAbstractHttpService } from '../../../core/http/services/http.abstract.service';
import { ShPaginationService } from '../../../core/http/services/pagination.service';
import { SortInfo } from '../../../overviews/generic-overview/components/display-settings-bar/entities/sort-info.entity';
import { PaginationInfo } from '../../../overviews/generic-overview/components/pagination/entities/pagination-info.entity';
import { SearchState } from '../../../overviews/search-page/entities/search-page-state.entity';
import { Identifier } from '../../records/entities/metadata/metadata.entity';
import { PartialRecord } from '../../records/entities/partial-record.class';
import { BatchActionData, BatchActions, BatchActionToObject } from '../entities/batch-actions.entity';
import { BatchObject, BatchStatus, emptyBatchObject } from '../entities/batch-object.entity';
import { BatchOptions, BatchTaskType, SearchRequest, ShBatchTaskTypes } from '../entities/batch-options.class';

@Injectable({ providedIn: 'root' })
export class BatchService {
    private readonly defaultHeaders: HttpHeaders = new HttpHeaders();
    private readonly batchesEndpoint: string = `v2/batches`;

    constructor(
        private readonly http: ShAbstractHttpService,
        private readonly paginationService: ShPaginationService
    ) {}

    private readonly batchMapping: { [key in BatchActions]: BatchActionToObject } = {
        [BatchActions.MERGE_MULTIPLE]: this.sendMergeMultipleBatch,
        [BatchActions.COUPLE_TO_PARENT]: this.coupleToParent,
        [BatchActions.REMOVE_FROM_INTELLECTUAL]: this.decoupleFromParent,
        [BatchActions.CSV_EXPORT]: this.startCSVBatch,
        [BatchActions.PUBLISH]: this.startPublishBatch,
        [BatchActions.DELETE]: this.startDeleteBatch
    };

    public sendBatch(batchAction: BatchActions, data: BatchActionData): Observable<BatchObject> {
        return this.batchMapping[batchAction].call(this, data);
    }

    // #region Merge Multiple Batch
    private sendMergeMultipleBatch(data: BatchActionData): Observable<BatchObject> {
        const { changes, searchRequest, isBulk, batchTag } = data;

        if (!changes || !searchRequest) {
            throw new Error(
                `BatchService:: expected data not to be undefined. Missing ${changes ? 'SearchRequest' : 'changes'}`
            );
        }

        // MED-3328: Do not send batch without sidecars, and act as if succeeded
        if (changes.length === 0) {
            return of(emptyBatchObject);
        }

        const batchOptions: BatchOptions<BatchTaskType> = new BatchOptions(
            ShBatchTaskTypes.MERGE_MULTIPLE,
            { Sidecars: changes.map((change: PartialRecord) => change.metadata) },
            searchRequest,
            batchTag ? batchTag : `Bulk edit by ${isBulk ? 'selection' : 'search result'}`
        );

        if (!batchOptions) {
            throw new Error(`BatchService:: expected batchOptions not to be undefined`);
        }

        return this.postBatchToEndpoint(batchOptions, data.waitForFinish);
    }
    // #endregion

    // #region Publish Batch
    private startPublishBatch(data: BatchActionData): Observable<BatchObject> {
        const { searchRequest, isBulk, batchTag } = data;
        if (!searchRequest) {
            throw new Error(`BatchService:: expected data not to be undefined. Missing 'SearchRequest'}`);
        }

        return this.sendPublishBatch(
            searchRequest,
            data.waitForFinish,
            batchTag ?? `Bulk publish by ${isBulk ? 'selection' : 'search result'}`
        );
    }

    private sendPublishBatch(
        searchRequest: SearchRequest,
        waitForFinish: boolean = false,
        batchTag: string = 'Batch publish'
    ): Observable<BatchObject> {
        const batchOptions: BatchOptions<BatchTaskType> = new BatchOptions<ShBatchTaskTypes.PUBLISH>(
            ShBatchTaskTypes.PUBLISH,
            { StrictValidation: true }, // This part is ignored, but adding it here too for clarity
            searchRequest,
            batchTag
        );

        if (!batchOptions) {
            throw new Error(`BatchService:: expected batchOptions not to be undefined`);
        }

        return this.postBatchToEndpoint(batchOptions, waitForFinish);
    }
    // #endregion

    // #region Couple to parent Batch
    private coupleToParent(data: BatchActionData): Observable<BatchObject> {
        const { parent, searchRequest, isBulk } = data;

        if (!parent || !searchRequest) {
            throw new Error(
                `BatchService:: expected data not to be undefined. Missing ${!parent ? 'parent' : 'SearchRequest'}`
            );
        }

        const ParentRecordId: Identifier = parent.getFragmentId();

        return this.sendAdoptBatch(
            ParentRecordId,
            searchRequest,
            data.waitForFinish,
            data.batchTag ? data.batchTag : `Bulk couple to parent by ${isBulk ? 'selection' : 'search result'}`
        );
    }

    private sendAdoptBatch(
        parentRecordId: Identifier | null,
        searchRequest: SearchRequest,
        waitForFinish: boolean = false,
        tag: string
    ): Observable<BatchObject> {
        if (!parentRecordId) {
            throw new Error(`BatchService:: expected ParentRecordId not to be undefined`);
        }

        const batchOptions: BatchOptions<BatchTaskType> = new BatchOptions(
            ShBatchTaskTypes.ADOPT,
            { ParentRecordId: parentRecordId },
            searchRequest,
            tag
        );

        if (!batchOptions) {
            throw new Error(`BatchService:: expected batchOptions not to be undefined`);
        }

        return this.postBatchToEndpoint(batchOptions, waitForFinish);
    }
    // #endregion

    // #region Decouple from parent Batch
    private decoupleFromParent(data: BatchActionData): Observable<BatchObject> {
        const { fragments, parent, searchRequest, isBulk } = data;

        if (!fragments || !searchRequest) {
            throw new Error(
                `BatchService:: expected data not to be undefined. Missing ${
                    !fragments ? 'fragments' : 'SearchRequest'
                }`
            );
        }

        const parentRecordId: Identifier | null = fragments[0].getClassificationId();

        return this.sendAdoptBatch(
            parentRecordId,
            searchRequest,
            data.waitForFinish,
            `Bulk decouple from parent by ${isBulk ? 'selection' : 'search result'}`
        );
    }
    // #endregion

    // #region Delete Batch
    private startDeleteBatch(data: BatchActionData): Observable<BatchObject> {
        const { searchRequest, batchTag } = data;
        if (!searchRequest) {
            throw new Error(`BatchService:: expected data not to be undefined. Missing 'SearchRequest'}`);
        }

        return this.sendDeleteBatch(searchRequest, data.waitForFinish, batchTag);
    }

    private sendDeleteBatch(
        searchRequest: SearchRequest,
        waitForFinish: boolean = false,
        batchTag: string = 'Batch delete'
    ): Observable<BatchObject> {
        const batchOptions: BatchOptions<BatchTaskType> = new BatchOptions<ShBatchTaskTypes.DELETE>(
            ShBatchTaskTypes.DELETE,
            { PreventCascade: false }, // Cascade deletion by default
            searchRequest,
            batchTag
        );

        return this.postBatchToEndpoint(batchOptions, waitForFinish);
    }
    // #endregion

    // #region CSV Batch
    private startCSVBatch(data: BatchActionData): Observable<BatchObject> {
        const { searchRequest, profileId, exportFileName } = data;
        if (!searchRequest || !profileId || !exportFileName) {
            throw new Error(
                `BatchService:: expected data not to be undefined. Missing 'SearchRequest | ProfileId | ExportFileName'}`
            );
        }

        return this.sendCsvBatch(
            profileId,
            exportFileName,
            searchRequest,
            data.waitForFinish,
            data.batchTag,
            data.headerType
        );
    }

    private sendCsvBatch(
        profileId: string,
        exportFileName: string,
        searchRequest: SearchRequest,
        waitForFinish: boolean = false,
        batchTag: string = 'BatchCSVTask',
        headerType?: string
    ): Observable<BatchObject> {
        const batchOptions: BatchOptions<BatchTaskType> = new BatchOptions<ShBatchTaskTypes.CSV_TASK>(
            ShBatchTaskTypes.CSV_TASK,
            {
                ProfileId: profileId,
                ExportFileName: exportFileName,
                HeaderType: headerType
            },
            searchRequest,
            batchTag
        );

        if (!batchOptions) {
            throw new Error(`BatchService:: expected batchOptions not to be undefined`);
        }

        return this.postBatchToEndpoint(batchOptions, waitForFinish);
    }
    // #endregion

    // #region POST
    private postBatchToEndpoint(
        batchOptions: BatchOptions<BatchTaskType>,
        waitForFinish: boolean = false
    ): Observable<BatchObject> {
        return this.http.post<BatchObject>(this.batchesEndpoint, batchOptions).pipe(
            switchMap((batchObject: BatchObject) => {
                return waitForFinish ? this.pollBatchFinished(batchObject.Id) : of(batchObject);
            })
        );
    }
    // #endregion

    // #region GET
    public getBatchById(batchId: string): Observable<BatchObject | undefined> {
        const url: string = `${this.batchesEndpoint}/${batchId}`;

        return this.http.get<BatchObject>(url).pipe(
            catchError(error => {
                console.warn(`BatchService:: No batch found with id ${batchId}`);
                return of(undefined);
            })
        );
    }

    public getBatchList(
        searchState: SearchState & { tag?: string; taskType?: string[] },
        index?: string,
        initiatorId?: string,
        status?: string
    ): Observable<Paginated<BatchObject>> {
        const paginationInfo: PaginationInfo = {
            page: searchState.page,
            resultsPerPage: searchState.pageSize ?? 0
        };
        const sortInfo: SortInfo = searchState.sortInfo;

        let params: HttpParams = new HttpParams({ encoder: new CustomHttpParamEncoder() })
            .set('sort', sortInfo.field)
            .set('direction', sortInfo.direction);

        params = index ? params.set('index', index) : params;
        params = initiatorId ? params.set('UserId', initiatorId) : params;

        searchState.structuredQuery.Fields?.forEach(queryPart => {
            params = params.set(queryPart.field, `*${queryPart.values.join('')}*`);
        });

        if (status) {
            params = params.set('Status', status);
        } else {
            params = params.delete('Status');
        }

        if (searchState.tag) {
            params = params.set('tag', searchState.tag);
        }
        if (searchState.taskType) {
            searchState.taskType.forEach(value => {
                params = params.append('taskType', value);
            });
        }

        const startIndex: number = paginationInfo.page * paginationInfo.resultsPerPage;

        return this.paginationService.get<BatchObject>(
            this.batchesEndpoint,
            params,
            this.defaultHeaders,
            startIndex,
            paginationInfo.resultsPerPage
        );
    }

    public getRunningBatch(userId: string): Observable<Paginated<BatchObject>> {
        const startIndex = 0;
        const resultAmount = 1;

        const params: HttpParams = new HttpParams({ encoder: new CustomHttpParamEncoder() })
            .set('sort', 'CreationDate')
            .set('direction', 'Desc')
            .set('tag', 'Bulk * search result')
            .set('userId', userId);

        return this.paginationService.get<BatchObject>(
            this.batchesEndpoint,
            params,
            this.defaultHeaders,
            startIndex,
            resultAmount
        );
    }

    public hasActiveBatch(userId: string, tag: string, taskType: string): Observable<boolean> {
        const startIndex = 0;
        const resultAmount = 0;

        const params: HttpParams = new HttpParams({ encoder: new CustomHttpParamEncoder() })
            .set('tag', tag)
            .set('userId', userId)
            .set('taskType', taskType)
            .append('status', BatchStatus.WAITING)
            .append('status', BatchStatus.PROCESSING);

        return this.paginationService
            .get<BatchObject>(this.batchesEndpoint, params, this.defaultHeaders, startIndex, resultAmount)
            .pipe(
                map(page => {
                    return page.TotalNrOfResults > 0;
                })
            );
    }

    public pollBatchFinished(batchId: string): Observable<BatchObject> {
        const request = `${this.batchesEndpoint}/${batchId}`;
        return this.http.get<BatchObject>(request).pipe(
            expand((batchObject: BatchObject) =>
                batchObject.FinishDate ? EMPTY : this.http.get<BatchObject>(request).pipe(delay(1000))
            ),
            last()
        );
    }
    // #endregion
}
